import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Favorite {
  id: string;
  user_id: string;
  service_id: number;
  created_at: string;
}

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    checkAuthAndFetchFavorites();
  }, []);

  const checkAuthAndFetchFavorites = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
      
      if (!session) {
        setLoading(false);
        return;
      }

      await fetchFavorites();
    } catch (error) {
      console.error('Error checking auth status:', error);
      toast({
        variant: "destructive",
        title: "Connection issue",
        description: "Could not verify authentication status. Check your connection."
      });
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('favorites')
        .select('*')
        .eq('user_id', session.user.id);

      if (error) throw error;
      
      setFavorites(data as Favorite[] || []);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast({
        variant: "destructive",
        title: "Connection issue",
        description: "Could not load your favorites. Check your connection."
      });
    } finally {
      setLoading(false);
    }
  };

  const addFavorite = async (serviceId: number) => {
    setIsSyncing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          variant: "destructive",
          title: "Authentication required",
          description: "Please sign in to add favorites"
        });
        return false;
      }

      const tempId = `temp-${Date.now()}`;
      const optimisticFavorite: Favorite = {
        id: tempId,
        user_id: session.user.id,
        service_id: serviceId,
        created_at: new Date().toISOString()
      };
      
      setFavorites(prev => [...prev, optimisticFavorite]);

      const { error, data } = await supabase
        .from('favorites')
        .insert({
          service_id: serviceId,
          user_id: session.user.id
        })
        .select('*')
        .single();

      if (error) {
        setFavorites(prev => prev.filter(fav => fav.id !== tempId));
        throw error;
      }

      setFavorites(prev => prev.map(fav => 
        fav.id === tempId ? (data as Favorite) : fav
      ));

      toast({
        title: "Success",
        description: "Added to favorites"
      });
      
      return true;
    } catch (error: any) {
      console.error('Error adding favorite:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to add to favorites. Please try again."
      });
      return false;
    } finally {
      setIsSyncing(false);
    }
  };

  const removeFavorite = async (serviceId: number) => {
    setIsSyncing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          variant: "destructive",
          title: "Authentication required",
          description: "Please sign in to manage favorites"
        });
        return false;
      }

      const previousFavorites = [...favorites];
      
      const favoriteToRemove = favorites.find(fav => fav.service_id === serviceId);
      if (favoriteToRemove) {
        setFavorites(prev => prev.filter(fav => fav.service_id !== serviceId));
      }

      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('service_id', serviceId)
        .eq('user_id', session.user.id);

      if (error) {
        setFavorites(previousFavorites);
        throw error;
      }

      toast({
        title: "Success",
        description: "Removed from favorites"
      });
      
      return true;
    } catch (error: any) {
      console.error('Error removing favorite:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to remove from favorites. Please try again."
      });
      return false;
    } finally {
      setIsSyncing(false);
    }
  };

  const isFavorite = (serviceId: number): boolean => {
    return favorites.some(fav => fav.service_id === serviceId);
  };

  return {
    favorites,
    loading,
    isSyncing,
    isAuthenticated,
    addFavorite,
    removeFavorite,
    isFavorite,
    refreshFavorites: fetchFavorites
  };
};
